import React, { useState } from 'react';
import { Button, Card, Space, Tag } from 'antd';
import { DVIInspectionFull, DVIInspectionItem } from '../../types/dvi';
import { DVISummaryCard } from './DVISummaryCard';
import { getInspectionState } from './inspectionState';

const WORST_COLOR = { pass: 'green', attention: 'gold', fail: 'red' } as const;

const STATUS_TAG: Record<DVIInspectionItem['status'], { color: string; label: string }> = {
  pending: { color: 'default', label: 'Pending' },
  pass: { color: 'green', label: 'Pass' },
  attention: { color: 'gold', label: 'Attention' },
  fail: { color: 'red', label: 'Fail' },
};

/** Glanceable one-tag status for headers. Same state logic as the card. */
export function InspectionStatusChip({ inspection }: { inspection: DVIInspectionFull | null }) {
  const s = getInspectionState(inspection);
  const style: React.CSSProperties = { fontSize: 11, padding: '1px 8px', margin: 0 };
  if (s.kind === 'none') return <Tag color="orange" style={style}>INSPECTION NEEDED</Tag>;
  if (s.kind === 'in_progress') return <Tag color="blue" style={style}>INSPECTION {s.done}/{s.total}</Tag>;
  return <Tag color={WORST_COLOR[s.worst]} style={style}>INSPECTION ✓</Tag>;
}

/**
 * Read-only inspection status for detail views (estimate view, and anywhere
 * else that only needs to SEE the inspection). Completed inspections reuse
 * DVISummaryCard; drafts get a progress summary with an expandable point list;
 * missing inspections show a "needed" notice (starting happens in the editor).
 */
export function InspectionStatusCard({ inspection }: { inspection: DVIInspectionFull | null }) {
  const [expanded, setExpanded] = useState(false);
  const s = getInspectionState(inspection);

  if (s.kind === 'complete' && inspection) {
    return <DVISummaryCard inspection={inspection} />;
  }

  if (s.kind === 'none' || !inspection) {
    return (
      <Card title="Vehicle Inspection" extra={<Tag color="orange">Inspection needed</Tag>}>
        <span style={{ color: '#666', fontSize: 13 }}>No inspection has been performed yet.</span>
      </Card>
    );
  }

  // in_progress
  if (s.kind !== 'in_progress' || !inspection) {
    return null;
  }

  const byCategory = new Map<string, DVIInspectionItem[]>();
  for (const it of inspection.items) {
    const list = byCategory.get(it.category_name) ?? [];
    list.push(it);
    byCategory.set(it.category_name, list);
  }

  return (
    <Card
      title={`Vehicle Inspection${inspection.template_name ? ` — ${inspection.template_name}` : ''}`}
      extra={<Tag color="blue">In Progress</Tag>}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <div style={{ color: '#666', fontSize: 13 }}>{s.done} of {s.total} points checked</div>
        <Space wrap>
          <Tag color="green">✓ {s.counts.pass} Passed</Tag>
          <Tag color="gold">⚠ {s.counts.attention} Attention</Tag>
          <Tag color="red">✗ {s.counts.fail} Failed</Tag>
        </Space>
        <Button onClick={() => setExpanded(e => !e)}>
          {expanded ? 'Hide points' : 'View points'}
        </Button>
        {expanded && Array.from(byCategory.entries()).map(([catName, items]) => (
          <div key={catName} style={{ marginTop: 8 }}>
            <h4 style={{ margin: 0, fontSize: 13, textTransform: 'uppercase' }}>{catName}</h4>
            {items.map(it => {
              const t = STATUS_TAG[it.status];
              const type = it.point_type ?? 'status';
              return (
                <div key={it.id} style={{ marginLeft: 12, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <Tag color={t.color} style={{ margin: 0, fontSize: 11 }}>{t.label}</Tag>
                  <span>{it.point_name}</span>
                  {type === 'reading' && (
                    <span style={{ color: '#888' }}>
                      {it.reading_value?.trim() ? `${it.reading_value}${it.unit ? ` ${it.unit}` : ''}` : '— no reading'}
                    </span>
                  )}
                  {type === 'photo' && (
                    <span style={{ color: '#888' }}>📷 {it.photos.length}</span>
                  )}
                  {it.notes && <span style={{ color: '#888' }}>— {it.notes}</span>}
                </div>
              );
            })}
          </div>
        ))}
      </Space>
    </Card>
  );
}

export default InspectionStatusCard;
